import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useDebouncedAutoSave } from 'shared/hooks/useDebouncedAutoSave';
import { clearDraft, loadDraft, saveDraft } from 'shared/lib/storage';
import type {
  CourierBankAccountInfo,
  CourierExperienceInfo,
  CourierOnboardingState,
  CourierPersonalInfo,
  CourierVehicleInfo,
  CourierDocument,
  CourierDocumentKey,
  CourierOcrExtraction,
  VehicleType,
} from 'features/courier-onboarding/types';

const initialState: CourierOnboardingState = {
  personalInfo: {
    fullName: '',
    phoneNumber: '',
    email: '',
    taxId: '',
    dateOfBirth: '',
    city: '',
    spokenLanguages: [],
  },
  vehicleInfo: { vehicleTypes: [] },
  experienceInfo: { hasDeliveryExperience: null, yearsOfExperienceRange: null },
  bankAccountInfo: {
    businessName: '',
    bankCode: '',
    bankName: '',
    bankBranchNumber: '',
    bankAccountNumber: '',
    bankAccountOwnerName: '',
    bankAccountOwnerId: '',
  },
  documents: [],
  ocrExtractions: {},
  notes: '',
  metadata: { documentsFirst: false, businessTrack: null },
};

/**
 * Merge a previously persisted draft on top of the fresh `initialState` so
 * newly added fields keep their defaults and obviously-stale entries are
 * dropped (e.g. uploaded documents whose remote URL is no longer present).
 */
const hydrateDraft = (
  draft: Partial<CourierOnboardingState> | null,
): CourierOnboardingState => {
  if (!draft) return initialState;
  const documents = Array.isArray(draft.documents)
    ? draft.documents.filter(
        (d): d is CourierDocument =>
          !!d && typeof d.url === 'string' && d.url.length > 0,
      )
    : initialState.documents;
  // Backward compatibility: older drafts stored a single `vehicleType` string.
  const legacyVehicle = (draft.vehicleInfo ?? {}) as {
    vehicleType?: VehicleType | '';
    vehicleTypes?: VehicleType[];
  };
  const vehicleTypes: VehicleType[] = Array.isArray(legacyVehicle.vehicleTypes)
    ? legacyVehicle.vehicleTypes
    : legacyVehicle.vehicleType
      ? [legacyVehicle.vehicleType]
      : [];
  return {
    ...initialState,
    ...draft,
    personalInfo: { ...initialState.personalInfo, ...(draft.personalInfo ?? {}) },
    vehicleInfo: { vehicleTypes },
    ocrExtractions: {
      ...initialState.ocrExtractions,
      ...(draft.ocrExtractions ?? {}),
    },
    experienceInfo: {
      ...initialState.experienceInfo,
      ...(draft.experienceInfo ?? {}),
    },
    bankAccountInfo: {
      ...initialState.bankAccountInfo,
      ...(draft.bankAccountInfo ?? {}),
    },
    documents,
    metadata: { ...initialState.metadata, ...(draft.metadata ?? {}) },
  };
};

interface CourierOnboardingContextValue {
  data: CourierOnboardingState;
  setPersonal: (partial: Partial<CourierPersonalInfo>) => void;
  setVehicle: (partial: Partial<CourierVehicleInfo>) => void;
  setExperience: (partial: Partial<CourierExperienceInfo>) => void;
  setBankAccount: (partial: Partial<CourierBankAccountInfo>) => void;
  setDocuments: (docs: CourierDocument[]) => void;
  upsertDocument: (doc: CourierDocument) => void;
  removeDocument: (key: CourierDocument['key']) => void;
  setDocumentOcrStatus: (
    key: CourierDocumentKey,
    status: CourierDocument['ocrStatus'],
  ) => void;
  setOcrExtraction: (
    key: CourierDocumentKey,
    extraction: CourierOcrExtraction | null,
  ) => void;
  setMetadata: (
    partial: Partial<CourierOnboardingState['metadata']>,
  ) => void;
  reset: () => void;
}

const Ctx = createContext<CourierOnboardingContextValue | undefined>(undefined);

export const CourierOnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<CourierOnboardingState>(() =>
    hydrateDraft(loadDraft<Partial<CourierOnboardingState>>()),
  );

  useDebouncedAutoSave(data, saveDraft);

  const setPersonal = useCallback((partial: Partial<CourierPersonalInfo>) => {
    setData((d) => ({ ...d, personalInfo: { ...d.personalInfo, ...partial } }));
  }, []);

  const setVehicle = useCallback((partial: Partial<CourierVehicleInfo>) => {
    setData((d) => ({ ...d, vehicleInfo: { ...d.vehicleInfo, ...partial } }));
  }, []);

  const setExperience = useCallback((partial: Partial<CourierExperienceInfo>) => {
    setData((d) => ({
      ...d,
      experienceInfo: { ...d.experienceInfo, ...partial },
    }));
  }, []);

  const setBankAccount = useCallback(
    (partial: Partial<CourierBankAccountInfo>) => {
      setData((d) => ({
        ...d,
        bankAccountInfo: { ...d.bankAccountInfo, ...partial },
      }));
    },
    [],
  );

  const setDocuments = useCallback((docs: CourierDocument[]) => {
    setData((d) => ({ ...d, documents: docs }));
  }, []);

  const upsertDocument = useCallback((doc: CourierDocument) => {
    setData((d) => {
      const next = d.documents.filter((x) => x.key !== doc.key);
      next.push(doc);
      return { ...d, documents: next };
    });
  }, []);

  const removeDocument = useCallback((key: CourierDocument['key']) => {
    setData((d) => ({
      ...d,
      documents: d.documents.filter((x) => x.key !== key),
    }));
  }, []);

  const setDocumentOcrStatus = useCallback(
    (key: CourierDocumentKey, status: CourierDocument['ocrStatus']) => {
      setData((d) => ({
        ...d,
        documents: d.documents.map((document) =>
          document.key === key
            ? { ...document, ocrStatus: status }
            : document,
        ),
      }));
    },
    [],
  );

  const setOcrExtraction = useCallback(
    (key: CourierDocumentKey, extraction: CourierOcrExtraction | null) => {
      setData((d) => {
        const next = { ...d.ocrExtractions };
        if (extraction === null) delete next[key];
        else next[key] = extraction;
        return { ...d, ocrExtractions: next };
      });
    },
    [],
  );

  const setMetadata = useCallback(
    (partial: Partial<CourierOnboardingState['metadata']>) => {
      setData((d) => ({ ...d, metadata: { ...d.metadata, ...partial } }));
    },
    [],
  );

  const reset = useCallback(() => {
    clearDraft();
    setData(initialState);
  }, []);

  const value = useMemo(
    () => ({
      data,
      setPersonal,
      setVehicle,
      setExperience,
      setBankAccount,
      setDocuments,
      upsertDocument,
      removeDocument,
      setDocumentOcrStatus,
      setOcrExtraction,
      setMetadata,
      reset,
    }),
    [
      data,
      setPersonal,
      setVehicle,
      setExperience,
      setBankAccount,
      setDocuments,
      upsertDocument,
      removeDocument,
      setDocumentOcrStatus,
      setOcrExtraction,
      setMetadata,
      reset,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCourierOnboarding = (): CourierOnboardingContextValue => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      'useCourierOnboarding must be used inside CourierOnboardingProvider',
    );
  }
  return ctx;
};
